import React, { useEffect, useState, useRef } from "react";
import { useParams } from "react-router-dom";
import { Loader2, FileCheck2, ShieldCheck, MapPin, Check, Eraser, MessageSquarePlus, Download, CreditCard, CheckCircle2 } from "lucide-react";
import { fmtD } from "../lib/date";
import { OwnerNotifications } from "../components/OwnerNotifications";
import { OwnerPlanReview } from "../components/OwnerPlanReview";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const api = (token) => `${BACKEND}/api/design/owner-portal/${token}`;

const DS_STYLE = { not_started: "bg-slate-100 text-slate-400", waiting_site_measure: "bg-amber-100 text-amber-700", preparing: "bg-sky-100 text-sky-700", requested: "bg-teal-100 text-teal-700", working: "bg-blue-100 text-blue-700", submitted: "bg-violet-100 text-violet-700", in_plan_check: "bg-indigo-100 text-indigo-700", correction: "bg-orange-100 text-orange-700", stuck: "bg-red-100 text-red-700", approved: "bg-emerald-100 text-emerald-700", done: "bg-emerald-200 text-emerald-800", exempt: "bg-slate-200 text-slate-500" };

function OwnerProgressStrip({ token }) {
  const [p, setP] = useState(null);
  useEffect(() => { fetch(`${api(token)}/progress`).then((r) => (r.ok ? r.json() : null)).then(setP).catch(() => setP(null)); }, [token]);
  if (!p) return null;
  return (
    <div className="bg-slate-900 text-white" data-testid="owner-portal-progress">
      <div className="max-w-5xl mx-auto px-5 py-3 space-y-2">
        <div className="flex flex-wrap items-center gap-3">
          <span className="text-xs text-slate-300">设计进度 Design Progress</span>
          <span className="text-sm font-bold" data-testid="owner-portal-ws">{p.workStatusName} <span className="text-slate-400 font-normal">{p.workStatusEn}</span></span>
          <span className="ml-auto text-sm font-bold" data-testid="owner-portal-pct">{p.progress == null ? "—" : `${p.progress}%`}</span>
        </div>
        <div className="h-1.5 rounded-full bg-white/15 overflow-hidden"><div className="h-full bg-emerald-400 rounded-full" style={{ width: `${p.progress ?? 0}%` }} /></div>
        <div className="flex gap-1 overflow-x-auto pb-0.5">{p.steps.map((s) => <span key={s.key} data-testid={`owner-portal-step-${s.key}`} className={`shrink-0 text-[10px] px-2 py-0.5 rounded-full whitespace-nowrap ${s.done ? "bg-emerald-500/30 text-emerald-200" : s.current ? "bg-white text-slate-900 font-bold" : "bg-white/10 text-slate-400"}`}>{s.done ? "✓ " : ""}{s.name} <span className="opacity-70">{s.en}</span></span>)}</div>
        <div className="flex gap-1 flex-wrap">{p.disciplines.map((d) => <span key={d.key} data-testid={`owner-portal-disc-${d.key}`} className={`text-[10px] px-1.5 py-0.5 rounded ${DS_STYLE[d.status] || DS_STYLE.not_started}`}>{d.name} · {d.label}</span>)}</div>
        {p.targetDate && <p className="text-[11px] text-slate-300" data-testid="owner-portal-target">Current stage target date 当前阶段目标日期：<b className="text-white">{fmtD(p.targetDate)}</b></p>}
      </div>
      {(p.drawings || []).length > 0 && (
        <div className="bg-emerald-50 border-t border-emerald-100 text-slate-800">
          <div className="max-w-5xl mx-auto px-5 py-3" data-testid="owner-portal-drawings">
            <p className="text-xs font-bold text-emerald-800 mb-2">已批准图纸 Approved Drawings · {p.drawings.length}</p>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-2">
              {p.drawings.map((f) => (
                <a key={f.aid} href={`${api(token)}/drawings/${f.aid}`} target="_blank" rel="noreferrer" data-testid={`owner-portal-drawing-${f.key}`} className="block rounded-xl border border-emerald-200 bg-white p-2.5 hover:border-emerald-500 hover:shadow transition">
                  <p className="text-xs font-bold text-slate-800 truncate">{f.name} <span className="text-slate-400 font-normal">{f.zh}</span></p>
                  <p className="text-[10px] text-emerald-700 font-semibold">{f.label} · v{f.version}</p>
                  <p className="text-[10px] text-slate-500 truncate">{f.fileName} · {(f.ext || "").toUpperCase()}{f.previewable ? " · 点击预览" : " · 点击下载"}</p>
                </a>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const PAY_LABEL = { Pending: "未到期", Due: "待付款 Due", Invoiced: "已开票 Invoiced", Paid: "已收款 Paid" };
function OwnerPayments({ payments }) {
  const due = payments.filter((x) => x.payUrl);
  if (!payments.length) return null;
  return (
    <div className="bg-amber-50 border-b border-amber-100" data-testid="owner-portal-payments">
      <div className="max-w-5xl mx-auto px-5 py-3">
        <p className="text-xs font-bold text-amber-800 mb-2 flex items-center gap-1.5"><CreditCard className="h-3.5 w-3.5" />设计费里程碑 Design Fee Milestones{due.length > 0 && <span className="ml-1 text-[10px] bg-amber-500 text-white rounded-full px-1.5" data-testid="owner-portal-pay-due-count">{due.length} 笔待付</span>}</p>
        <div className="grid sm:grid-cols-3 gap-2">
          {payments.map((x) => (
            <div key={x.id} data-testid={`owner-portal-pay-${x.id}`} data-status={x.status} className={`rounded-xl border p-2.5 flex items-center gap-2 ${x.payUrl ? "bg-white border-amber-300" : x.status === "Paid" ? "bg-emerald-50 border-emerald-200" : "bg-white/60 border-slate-200"}`}>
              <div className="flex-1 min-w-0">
                <p className="text-xs font-bold text-slate-800 truncate">{x.milestone} <span className="text-slate-400 font-normal">{x.pct}%</span></p>
                <p className="text-[11px] text-slate-600">${Number(x.amount || 0).toLocaleString()} · {x.status === "Paid" ? <span className="text-emerald-700 inline-flex items-center gap-0.5"><CheckCircle2 className="h-3 w-3" />{PAY_LABEL.Paid}</span> : x.proofPending ? <span className="text-amber-700">凭证核销中</span> : PAY_LABEL[x.status] || x.status}</p>
              </div>
              {x.payUrl && <a href={x.payUrl} target="_blank" rel="noreferrer" data-testid={`owner-portal-pay-now-${x.id}`} className="shrink-0 text-[12px] font-bold px-3 py-1.5 rounded-full bg-slate-900 text-white hover:bg-slate-700">{x.proofPending ? "查看/补传凭证" : "立即支付 Pay"}</a>}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function SignaturePad({ onChange }) {
  const ref = useRef();
  const drawing = useRef(false);
  const dirty = useRef(false);
  const pos = (e) => { const c = ref.current; const r = c.getBoundingClientRect(); const t = e.touches ? e.touches[0] : e; return { x: (t.clientX - r.left) * (c.width / r.width), y: (t.clientY - r.top) * (c.height / r.height) }; };
  const start = (e) => { drawing.current = true; const ctx = ref.current.getContext("2d"); const p = pos(e); ctx.beginPath(); ctx.moveTo(p.x, p.y); e.preventDefault(); };
  const move = (e) => { if (!drawing.current) return; const ctx = ref.current.getContext("2d"); const p = pos(e); ctx.lineTo(p.x, p.y); ctx.strokeStyle = "#0f172a"; ctx.lineWidth = 2.5; ctx.lineCap = "round"; ctx.stroke(); dirty.current = true; e.preventDefault(); };
  const end = () => { if (drawing.current && dirty.current) onChange(ref.current.toDataURL("image/png")); drawing.current = false; };
  const clear = () => { const c = ref.current; c.getContext("2d").clearRect(0, 0, c.width, c.height); dirty.current = false; onChange(null); };
  return (
    <div>
      <canvas ref={ref} width={520} height={150} data-testid="owner-portal-sign-pad"
        className="w-full h-36 rounded-lg border-2 border-dashed border-slate-300 bg-slate-50 touch-none cursor-crosshair"
        onMouseDown={start} onMouseMove={move} onMouseUp={end} onMouseLeave={end}
        onTouchStart={start} onTouchMove={move} onTouchEnd={end} />
      <button onClick={clear} className="mt-1 text-[12px] text-slate-400 hover:text-red-500 flex items-center gap-1"><Eraser className="h-3 w-3" />清除重签 Clear</button>
    </div>
  );
}

export default function OwnerDesignPortal() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [active, setActive] = useState(null); // active file
  const [markups, setMarkups] = useState([]);
  const [markMode, setMarkMode] = useState(false);
  const [signer, setSigner] = useState("");
  const [confirm, setConfirm] = useState(false);
  const [signerEmail, setSignerEmail] = useState("");
  const [sig, setSig] = useState(null);
  const [receipt, setReceipt] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const loadMarkups = () => fetch(`${api(token)}/markups`).then((r) => r.json()).then((d) => setMarkups(d.markups || [])).catch(() => {});
  useEffect(() => {
    fetch(api(token)).then((r) => { if (!r.ok) throw new Error(); return r.json(); })
      .then((d) => { setData(d); setActive((d.files || [])[0] || null); }).catch(() => setErr("链接无效或已失效 · Link invalid or expired"));
    loadMarkups();
  }, [token]);

  const addMarkup = async (e) => {
    if (!markMode || !active) return;
    const r = e.currentTarget.getBoundingClientRect();
    const x = ((e.clientX - r.left) / r.width) * 100;
    const y = ((e.clientY - r.top) / r.height) * 100;
    const text = window.prompt("批注内容 · Your comment");
    if (!text) return;
    await fetch(`${api(token)}/markup`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ fileAid: active.aid, x, y, text }) });
    loadMarkups();
  };

  const submitSign = async () => {
    if (!signer.trim()) return alert("请填写签署人姓名 · Enter your name");
    if (!confirm) return alert("请勾选确认 · Please confirm");
    setSubmitting(true);
    try {
      const res = await fetch(`${api(token)}/sign`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ fileAid: active.aid, signerName: signer, signerEmail: signerEmail.trim() || undefined, confirmText: "本人已审阅并同意该方案 I have reviewed and approve this plan", signature: sig }) });
      const d = await res.json();
      if (!res.ok) throw new Error();
      setReceipt(d.receipt);
    } catch { alert("提交失败，请重试 · Failed"); } finally { setSubmitting(false); }
  };

  if (err) return <div className="min-h-screen flex items-center justify-center text-slate-500 p-6 text-center" data-testid="owner-portal-error">{err}</div>;
  if (!data) return <div className="min-h-screen flex items-center justify-center text-slate-400"><Loader2 className="h-6 w-6 animate-spin mr-2" />加载中 Loading…</div>;

  const activeMarkups = markups.filter((m) => m.fileAid === active?.aid);

  return (
    <div className="min-h-screen bg-slate-100" data-testid="owner-portal-page">
      <div className="bg-white border-b border-slate-200">
        <div className="max-w-5xl mx-auto px-5 py-4">
          <p className="text-xs font-bold text-indigo-600">ALIGCUS · 业主方案确认 Owner Approval</p>
          <h1 className="text-xl font-bold text-slate-800" data-keep-title>{data.project.address}</h1>
          <p className="text-sm text-slate-500">{data.project.city} · {data.project.type} · {data.project.projectId}</p>
        </div>
      </div>
      <OwnerProgressStrip token={token} />
      <OwnerPayments payments={data.payments || []} />

      <div className="max-w-5xl mx-auto px-5 py-5 grid md:grid-cols-4 gap-4">
        {/* file list */}
        <div className="md:col-span-1 space-y-2" data-testid="owner-portal-files">
          <p className="text-xs font-semibold text-slate-500">已发布方案 Published Plans</p>
          {data.files.length === 0 && <p className="text-sm text-slate-400">暂无已批准的方案 No approved plans yet</p>}
          {data.files.map((f) => (
            <button key={f.aid} onClick={() => { setActive(f); setReceipt(null); }} data-testid={`owner-portal-file-${f.aid}`}
              className={`w-full text-left rounded-lg border p-2.5 ${active?.aid === f.aid ? "border-indigo-400 bg-indigo-50" : "border-slate-200 bg-white"}`}>
              <p className="text-[13px] text-slate-700 flex items-center gap-1"><FileCheck2 className="h-3.5 w-3.5 text-emerald-500" />{f.type} v{f.version}</p>
              <p className="text-[10px] text-slate-400 truncate">{f.name}</p>
            </button>
          ))}
          {(data.signed || []).length > 0 && (
            <div className="pt-2" data-testid="owner-portal-signed-list">
              <p className="text-xs font-semibold text-slate-500 mb-1">我的签字文件 My signed documents</p>
              {data.signed.map((s) => (
                <div key={s.id} className="rounded-lg border border-emerald-200 bg-emerald-50/60 p-2 mb-1.5" data-testid={`owner-portal-signed-${s.id}`}>
                  <p className="text-[12px] text-emerald-800 font-semibold flex items-center gap-1"><ShieldCheck className="h-3.5 w-3.5" />{s.fileType} v{s.fileVersion}</p>
                  <p className="text-[10px] text-slate-500">{s.signerName} · {fmtD(s.at)}</p>
                  {s.url ? <a href={`${BACKEND}${s.url}`} target="_blank" rel="noreferrer" data-testid={`owner-portal-signed-dl-${s.id}`} className="mt-1 inline-flex items-center gap-1 text-[11px] text-emerald-700 hover:underline"><Download className="h-3 w-3" />签字确认书 PDF</a> : <span className="text-[10px] text-slate-400">PDF 生成中</span>}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* viewer + sign */}
        <div className="md:col-span-3 space-y-4">
          {active && (
            <>
              <div className="bg-white rounded-xl border border-slate-200 p-3">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-sm font-semibold text-slate-700">{active.type} v{active.version}</p>
                  <div className="flex gap-2">
                    <button onClick={() => setMarkMode((v) => !v)} data-testid="owner-portal-markmode" className={`text-[12px] flex items-center gap-1 px-2.5 py-1 rounded-full ${markMode ? "bg-amber-500 text-white" : "bg-slate-100 text-slate-600"}`}><MessageSquarePlus className="h-3.5 w-3.5" />{markMode ? "批注中(点击图纸)" : "添加批注 Markup"}</button>
                    <a href={`${api(token)}/file/${active.aid}`} target="_blank" rel="noreferrer" className="text-[12px] flex items-center gap-1 px-2.5 py-1 rounded-full bg-slate-100 text-slate-600"><Download className="h-3.5 w-3.5" />下载</a>
                  </div>
                </div>
                <div className="relative w-full h-[62vh] rounded-lg overflow-hidden border border-slate-100 bg-slate-50" data-testid="owner-portal-viewer">
                  {active.ext === "pdf"
                    ? <iframe title={active.name} src={`${api(token)}/file/${active.aid}`} className="w-full h-full" />
                    : active.previewable
                      ? <img alt={active.name} src={`${api(token)}/file/${active.aid}`} className="w-full h-full object-contain" />
                      : <div className="flex items-center justify-center h-full text-slate-400 text-sm">该格式请下载查看</div>}
                  {/* markup overlay */}
                  <div onClick={addMarkup} data-testid="owner-portal-markoverlay"
                    className="absolute inset-0" style={{ pointerEvents: markMode ? "auto" : "none", cursor: markMode ? "crosshair" : "default" }}>
                    {activeMarkups.map((m) => (
                      <div key={m.id} title={m.text} data-testid={`owner-portal-pin-${m.id}`} className="absolute -translate-x-1/2 -translate-y-full" style={{ left: `${m.x}%`, top: `${m.y}%`, pointerEvents: "auto" }}>
                        <MapPin className="h-5 w-5 text-red-500 drop-shadow" fill="#fee2e2" />
                      </div>
                    ))}
                  </div>
                </div>
                {activeMarkups.length > 0 && (
                  <div className="mt-2 space-y-1" data-testid="owner-portal-markup-list">
                    {activeMarkups.map((m, i) => <p key={m.id} className="text-[12px] text-slate-600"><span className="text-red-500 font-semibold">#{i + 1}</span> {m.text}</p>)}
                  </div>
                )}
              </div>

              {/* review: confirm / request changes */}
              <OwnerPlanReview base={api(token)} activeAid={active.aid} />

              {/* sign */}
              {receipt ? (
                <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-5 text-center" data-testid="owner-portal-receipt">
                  <ShieldCheck className="h-10 w-10 text-emerald-500 mx-auto mb-2" />
                  <p className="text-base font-bold text-emerald-700">签署成功 · Approved & Signed</p>
                  <div className="mt-3 text-left text-[12px] text-slate-600 bg-white rounded-lg p-3 inline-block">
                    <p>签署人 Signer: <b>{receipt.signerName}</b></p>
                    <p>版本 Version: v{receipt.fileVersion}</p>
                    <p>文件指纹 SHA256: <span className="font-mono text-[10px]">{receipt.fileSha256}</span></p>
                    <p>存证编号 Record: <span className="font-mono text-[10px]">{receipt.recordHash}</span></p>
                    <p>时间 Time (UTC): {receipt.at}</p>
                    <p>IP: {receipt.ip}</p>
                  </div>
                  {receipt.ownerEmailed?.ok && <p className="text-[11px] text-emerald-700 mt-2" data-testid="owner-portal-emailed">✉ 签字确认书 PDF 已发送到 {receipt.ownerEmailed.to} · Signed copy emailed</p>}
                  {receipt.signedPdf && <a href={`${BACKEND}${receipt.signedPdf.url}`} target="_blank" rel="noreferrer" data-testid="owner-portal-signed-pdf" className="mt-3 inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-emerald-600 text-white text-sm font-semibold hover:bg-emerald-700"><Download className="h-4 w-4" />下载签字确认书 PDF · Signed copy</a>}
                  <p className="text-[10px] text-slate-400 mt-2">该记录具防篡改指纹，作为您对本方案版本的正式确认。{receipt.signedPdf ? " 签字确认书（原图纸 + 签署证书页）已同步给您的销售顾问与设计团队。" : ""}</p>
                </div>
              ) : (
                <div className="bg-white rounded-xl border border-slate-200 p-5" data-testid="owner-portal-sign">
                  <p className="text-sm font-semibold text-slate-700 mb-1">Step 2 · 方案确认与电子签署 Approve & Sign</p>
                  <p className="text-[12px] text-slate-500 mb-3">签署即表示您已审阅并同意 <b>{active.type} v{active.version}</b>。系统将记录版本指纹、时间与来源以防篡改。</p>
                  <input value={signer} onChange={(e) => setSigner(e.target.value)} placeholder="您的姓名 Your full name" data-testid="owner-portal-signer" className="w-full text-sm rounded-lg border border-slate-200 px-3 py-2 mb-2" />
                  <input type="email" value={signerEmail} onChange={(e) => setSignerEmail(e.target.value)} placeholder={data.ownerEmailMasked ? `接收签字确认书 PDF 的邮箱（可选，留空发送到 ${data.ownerEmailMasked}）` : "接收签字确认书 PDF 的邮箱（可选）· Email for your signed copy"} data-testid="owner-portal-signer-email" className="w-full text-sm rounded-lg border border-slate-200 px-3 py-2 mb-3" />
                  <SignaturePad onChange={setSig} />
                  <label className="flex items-start gap-2 mt-3 text-[13px] text-slate-600">
                    <input type="checkbox" checked={confirm} onChange={(e) => setConfirm(e.target.checked)} data-testid="owner-portal-confirm" className="mt-0.5 accent-indigo-600" />
                    本人已审阅并同意该方案 · I have reviewed and approve this plan.
                  </label>
                  <button onClick={submitSign} disabled={submitting} data-testid="owner-portal-submit" className="mt-3 w-full py-2.5 rounded-lg bg-indigo-600 text-white font-semibold disabled:opacity-50 flex items-center justify-center gap-2">
                    {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}确认并签署 Approve & Sign
                  </button>
                </div>
              )}
            </>
          )}
          {!active && <div className="bg-white rounded-xl border border-slate-200 p-10 text-center text-slate-400">暂无可确认的方案 No plans to approve yet</div>}
          <OwnerNotifications url={`${api(token)}/notifications`} zh />
        </div>
      </div>
    </div>
  );
}
